import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';
import ReactGA from 'react-ga4';

import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';

import './styles/GlobalStyles.css';
import './pages/Credits.css';

import LandingPage from './pages/LandingPage';
import ServiceProviders from './pages/ServiceProviders';
import PublicServiceProviderProfile from './pages/PublicServiceProviderProfile';
import AvailableJobsPublic from './pages/AvailableJobsPublic';
import PublicJobDetails from './pages/PublicJobDetails';
import Services from './pages/Services';
import Fleet from './pages/Fleet';
import HowItWorks from './pages/HowItWorks';
import HelpCenter from './pages/HelpCenter';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Security from './pages/Security';

import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import CompleteProfile from './pages/CompleteProfile';
import NotFound from './pages/NotFound';

import OwnerDashboard from './pages/OwnerDashboard';
import ServiceProviderDashboard from './pages/ServiceProviderDashboard';
import AdminDashboard from './pages/AdminDashboard';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Messages from './pages/Messages';
import ChatDetail from './pages/ChatDetail';
import Notifications from './pages/Notifications';
import Analytics from './pages/Analytics';
import SubmitQuotation from './pages/SubmitQuotation';
import EditQuotation from './pages/EditQuotation';
import PaymentVerificationsList from './pages/PaymentVerificationsList';
import DisputeDetail from './pages/DisputeDetail';
import MyQuotations from './pages/MyQuotations';
import MyJobsQuotations from './pages/MyJobsQuotations';
import AdminWithdrawals from './pages/AdminWithdrawals';

import Credits from './pages/Credits';
import BuyCredits from './pages/BuyCredits';
import CreditTransactions from './pages/CreditTransactions';
import AdminCreditWallets from './pages/admin/AdminCreditWallets';
import AdminCreditPurchases from './pages/admin/AdminCreditPurchases';

import PostJob from './components/owner/PostJob';
import MyShips from './components/owner/MyShips';
import OwnerPayments from './components/owner/Payments';
import Certificates from './components/owner/Certificates';
import Tasks from './components/owner/Tasks';
import OwnerJobDetail from './components/owner/JobDetail';
import ShipDetail from './components/owner/ShipDetail';
import EditJob from './components/owner/EditJob';
import OwnerContractDetail from './components/owner/ContractDetail';
import VendorProfile from './components/owner/VendorProfile';

import AvailableJobs from './components/vendor/AvailableJobs';
import ActiveWork from './components/vendor/ActiveWork';
import WorkReports from './components/vendor/WorkReports';
import ServiceProviderPayments from './components/vendor/Payments';
import CompanyProfile from './components/vendor/CompanyProfile';
import VendorContractDetail from './components/vendor/ContractDetail';
import VendorJobDetail from './components/vendor/JobDetail';
import VendorAnalytics from './components/vendor/VendorAnalytics';
import QuoteDetail from './components/vendor/QuoteDetail';

import VerifyServiceProviders from './components/admin/VerifyVendors';
import Disputes from './components/admin/Disputes';
import PaymentReleases from './components/admin/PaymentReleases';
import Users from './components/admin/Users';
import Reports from './components/admin/Reports';
import AdminSettings from './components/admin/Settings';
import UserDetail from './components/admin/UserDetail';
import AdminVendorDetail from './components/admin/VendorDetail';
import EscrowDetail from './components/admin/EscrowDetail';
import AuditLogs from './components/admin/AuditLogs';
import PaymentVerification from './components/admin/PaymentVerification';

import Header from './components/common/Header';
import Sidebar from './components/common/Sidebar';
import Footer from './components/common/Footer';

const publicRoutes = [
  ['/', LandingPage],
  ['/service-providers', ServiceProviders],
  ['/service-providers/:id', PublicServiceProviderProfile],
  ['/available-jobs', AvailableJobsPublic],
  ['/jobs/:id', PublicJobDetails],
  ['/services', Services],
  ['/fleet', Fleet],
  ['/how-it-works', HowItWorks],
  ['/help-center', HelpCenter],
  ['/privacy-policy', PrivacyPolicy],
  ['/terms-of-service', TermsOfService],
  ['/security', Security]
];

const authenticationRoutes = [
  ['/login', Login],
  ['/register', Register],
  ['/forgot-password', ForgotPassword],
  ['/reset-password/:token?', ResetPassword],
  ['/verify-email/:token?', VerifyEmail],
  ['/complete-profile', CompleteProfile]
];

const ownerRoutes = [
  ['/owner/dashboard', OwnerDashboard],
  ['/owner/my-ships', MyShips],
  ['/owner/ship/:id', ShipDetail],
  ['/owner/post-job', PostJob],
  ['/owner/my-jobs-quotations', MyJobsQuotations],
  ['/owner/my-jobs-bids', MyJobsQuotations],
  ['/owner/edit-job/:id', EditJob],
  ['/owner/job/:id', OwnerJobDetail],
  ['/owner/contract/:id', OwnerContractDetail],
  ['/owner/quote/:id', QuoteDetail],
  ['/owner/payments', OwnerPayments],
  ['/owner/certificates', Certificates],
  ['/owner/tasks', Tasks],
  ['/owner/analytics', Analytics],
  ['/owner/vendor/:id', VendorProfile],
  ['/credits', Credits],
  ['/credits/buy', BuyCredits],
  ['/credits/transactions', CreditTransactions],
  ['/owner/credits', Credits],
  ['/owner/credits/buy', BuyCredits],
  ['/owner/credits/transactions', CreditTransactions]
];

const providerRoutes = [
  ['/service-provider/dashboard', ServiceProviderDashboard],
  ['/service-provider/available-jobs', AvailableJobs],
  ['/service-provider/submit-quotation/:id', SubmitQuotation],
  ['/service-provider/edit-quotation/:id', EditQuotation],
  ['/service-provider/job/:id', VendorJobDetail],
  ['/service-provider/my-offers', MyQuotations],
  ['/service-provider/quote/:id', QuoteDetail],
  ['/service-provider/active-work', ActiveWork],
  ['/service-provider/contract/:id', VendorContractDetail],
  ['/service-provider/work-reports', WorkReports],
  ['/service-provider/payments', ServiceProviderPayments],
  ['/service-provider/company-profile', CompanyProfile],
  ['/service-provider/analytics', VendorAnalytics]
];

const adminRoutes = [
  ['/admin/dashboard', AdminDashboard],
  ['/admin/verify-service-providers', VerifyServiceProviders],
  ['/admin/vendor/:id', AdminVendorDetail],
  ['/admin/disputes', Disputes],
  ['/admin/dispute/:id', DisputeDetail],
  ['/admin/payment-releases', PaymentReleases],
  ['/admin/escrow/:id', EscrowDetail],
  ['/admin/users', Users],
  ['/admin/user/:id', UserDetail],
  ['/admin/reports', Reports],
  ['/admin/audit-logs', AuditLogs],
  ['/admin/settings', AdminSettings],
  ['/admin/payment-verification/:id', PaymentVerification],
  ['/admin/payment-verifications', PaymentVerificationsList],
  ['/admin/withdrawals', AdminWithdrawals],
  ['/admin/credit-wallets', AdminCreditWallets],
  ['/admin/credit-purchases', AdminCreditPurchases]
];

const sharedRoutes = [
  ['/:role/profile', Profile],
  ['/:role/settings', Settings],
  ['/messages', Messages],
  ['/messages/:id', ChatDetail],
  ['/notifications', Notifications]
];

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-spinner" role="status">
        Loading...
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: `${location.pathname}${location.search}`
        }}
      />
    );
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={`/${user.role}/dashboard`} replace />;
  }

  return children;
}

function MainLayout({ children, userRole }) {
  const { sidebarCollapsed } = useTheme();
  const { user } = useAuth();

  return (
    <div
      className={`app-layout${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}
    >
      <Sidebar userRole={userRole} />

      <div className="main-wrapper">
        <Header
          userRole={userRole}
          userName={user?.name || 'User'}
          sidebarCollapsed={sidebarCollapsed}
        />

        <main className="main-content">
          {children}
        </main>

        <Footer />
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname + location.search
    });
  }, [location.pathname, location.search]);

  return (
    <Routes>
      {publicRoutes.map(([path, Page]) => (
        <Route
          key={path}
          path={path}
          element={<Page />}
        />
      ))}

      {authenticationRoutes.map(([path, Page]) => (
        <Route
          key={path}
          path={path}
          element={<Page />}
        />
      ))}

      {ownerRoutes.map(([path, Page]) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <MainLayout userRole="owner">
                <Page />
              </MainLayout>
            </ProtectedRoute>
          }
        />
      ))}

      {providerRoutes.map(([path, Page]) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute allowedRoles={['service-provider']}>
              <MainLayout userRole="service-provider">
                <Page />
              </MainLayout>
            </ProtectedRoute>
          }
        />
      ))}

      {adminRoutes.map(([path, Page]) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout userRole="admin">
                <Page />
              </MainLayout>
            </ProtectedRoute>
          }
        />
      ))}

      {sharedRoutes.map(([path, Page]) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute>
              <MainLayout userRole={user?.role}>
                <Page />
              </MainLayout>
            </ProtectedRoute>
          }
        />
      ))}

      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <SocketProvider>
          <ThemeProvider>
            <AppRoutes />
          </ThemeProvider>
        </SocketProvider>
      </AuthProvider>
    </Router>
  );
}